const STANDARDS = [
	{
		title: "Clapio product facts",
		text: "Claims about supported platforms, recording features, pricing, and downloads are checked against the current Clapio product and release information.",
	},
	{
		title: "Competitor sources",
		text: "Comparison claims should come from each provider's official product, pricing, download, or documentation pages. Relevant comparison pages show the checked date and direct source links.",
	},
	{
		title: "Testing boundaries",
		text: "We distinguish published specifications from hands-on findings and do not present a feature comparison as an independent benchmark or laboratory test.",
	},
	{
		title: "Updates and corrections",
		text: "Materially revised guides show an updated date. Readers can report outdated prices, platform support, features, policies, or broken sources through the issue form.",
	},
];

const PRODUCT_FACTS = [
	{ label: "Platforms", value: "macOS, Windows, and Linux" },
	{ label: "Core workflow", value: "Record, edit, caption, and export" },
	{ label: "Processing", value: "Local-first with on-device transcription" },
	{ label: "Exports", value: "MP4, GIF, SRT, and VTT" },
];

export function AboutPage() {
	return (
		<section className="pb-24 pt-28 md:pb-32 md:pt-40">
			<div className="mx-auto max-w-4xl px-6">
				<div className="rounded-[2rem] border border-white/[0.08] bg-white/[0.04] p-7 shadow-2xl shadow-black/20 md:p-10">
					<p className="text-[11px] font-medium uppercase tracking-[0.18em] text-brand-300">
						About Clapio
					</p>
					<h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl">
						About Clapio, the local-first screen recorder and editor
					</h1>
					<p className="mt-6 max-w-3xl text-lg leading-8 text-white/58 md:text-xl">
						Clapio is desktop screen recording and editing software for Mac, Windows, and Linux. It
						records screen, webcam, microphone, and system audio, then keeps editing, captioning,
						and export in one local-first workflow.
					</p>
				</div>

				<div className="mt-10 space-y-10">
					<section>
						<h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
							What Clapio does
						</h2>
						<p className="mt-4 text-base leading-8 text-white/52 md:text-lg">
							Clapio is designed for product demos, tutorials, bug reports, training videos, and
							async updates. Its editor adds trimming, zoom, cursor treatment, annotations,
							backgrounds, device frames, and captions before a recording is exported as a file.
						</p>
						<div className="mt-6 grid gap-4 sm:grid-cols-2">
							{PRODUCT_FACTS.map((fact) => (
								<div key={fact.label} className="glass-card rounded-[1.25rem] p-5">
									<p className="text-[11px] font-medium uppercase tracking-[0.18em] text-brand-300">
										{fact.label}
									</p>
									<p className="mt-2 text-base font-medium text-white/85">{fact.value}</p>
								</div>
							))}
						</div>
					</section>

					<section className="rounded-[1.75rem] border border-white/[0.08] bg-white/[0.03] p-6 md:p-8">
						<h2 className="text-2xl font-semibold tracking-tight text-white">
							Official Clapio information
						</h2>
						<p className="mt-4 text-sm leading-7 text-white/60 md:text-base">
							Use the official product page for current features and pricing, the download section
							for platform installers, and the public release repository for version history.
						</p>
						<div className="mt-5 flex flex-wrap gap-3">
							<a
								href="/"
								className="rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-400"
							>
								Clapio product page
							</a>
							<a
								href="/#download"
								className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:text-white"
							>
								Official downloads
							</a>
							<a
								href="https://github.com/Dhaxor/clapio-release/releases"
								target="_blank"
								rel="noopener noreferrer"
								className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:text-white"
							>
								Release history
							</a>
						</div>
					</section>

					<section>
						<h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
							Editorial standards and commercial disclosure
						</h2>
						<p className="mt-4 text-base leading-8 text-white/52 md:text-lg">
							Clapio also publishes practical guides about recording, editing, platforms, and
							competing tools. This is a commercial product site, not an independent review
							publication. Automated tools may assist research, drafting, analysis, and quality
							checks. Pages should be judged by their official sources, checked dates, disclosures,
							and factual accuracy rather than unsupported claims of expertise.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
							How content is maintained
						</h2>
						<div className="mt-6 grid gap-4 md:grid-cols-2">
							{STANDARDS.map((standard) => (
								<div key={standard.title} className="glass-card rounded-[1.5rem] p-6">
									<h3 className="text-lg font-semibold text-white/90">{standard.title}</h3>
									<p className="mt-3 text-sm leading-7 text-white/48">{standard.text}</p>
								</div>
							))}
						</div>
					</section>

					<section>
						<h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
							Commercial disclosure
						</h2>
						<p className="mt-4 text-base leading-8 text-white/52 md:text-lg">
							Clapio earns revenue from its own paid software licenses. Articles that discuss Clapio
							or alternatives are therefore not independent reviews of Clapio. Buyer guides should
							explain product trade-offs, avoid fabricated ratings or universal winners, and
							acknowledge when another workflow may fit a reader better.
						</p>
					</section>

					<section className="rounded-[1.75rem] border border-brand-500/20 bg-brand-500/10 p-6 md:p-8">
						<h2 className="text-2xl font-semibold tracking-tight text-white">
							Support and corrections
						</h2>
						<p className="mt-4 text-sm leading-7 text-white/60 md:text-base">
							Found an outdated feature, price, platform claim, policy, or source? Include the page
							URL and the correction in our issue form so the page can be reviewed. For product,
							privacy, or terms questions, email{" "}
							<a
								href="mailto:hello@learn2vibecode.dev"
								className="text-brand-200 underline underline-offset-4"
							>
								hello@learn2vibecode.dev
							</a>
							.
						</p>
						<a
							href="https://docs.google.com/forms/d/e/1FAIpQLSe6-JvdMixskej51UvG5IdqbnTW_cqDlIDyQLPtEvvwFvkZZg/viewform?usp=publish-editor"
							target="_blank"
							rel="noopener noreferrer"
							className="mt-5 inline-flex rounded-xl bg-brand-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-400"
						>
							Report a factual issue
						</a>
					</section>
				</div>
			</div>
		</section>
	);
}
